import { InlineNotification } from '@carbon/react';

import type { ServerExportStatus } from 'lib/hooks/useServerExport';
import { format0 } from 'utils/number';

/**
 * Renders a Carbon `InlineNotification` synchronized with
 * `useServerExport`'s `exportStatus` for the full lifecycle:
 *
 *   idle       → renders nothing
 *   processing → kind="info"    ("Exporting…")
 *   complete   → kind="success" ("Export ready · N rows exported.")
 *   failed     → kind="error"   (server-provided error message)
 *
 * --- Why this exists ---
 *
 * The codebase has several consumers of `useServerExport`
 * (`pages/type-curves`, `pages/afe-catalog`, `components/free-cash-flow`,
 * etc.) but each surfaces lifecycle state differently — some only show
 * errors, some show nothing at all, and the `autoDownload` default is
 * treated as the implicit "feedback signal." Result: inconsistent UX
 * across exports.
 *
 * This component is the SKELETON of a cross-cutting solution. It's used by
 * the IC ComparisonTable, the asset-picker tearsheet and Map Query; it can
 * also replace the bespoke banners in:
 *
 *   - components/free-cash-flow/free-cash-flows-by-slot-table.tsx
 *     (currently error-only InlineNotification with its own state)
 *   - pages/type-curves/* (returns the raw hook; surfaces nothing today)
 *   - pages/afe-catalog/* (uses an atom-based path that doesn't even
 *     import useServerExport — larger refactor scope)
 *
 * --- Common-component skeleton notes ---
 *
 * A future "v2" of this component could absorb more of the wiring:
 *
 *   1. Accept the hook RESULT directly (status + resetExport in one prop)
 *      so callers pass `{...exportResult}` instead of two props.
 *   2. Take an optional `autoDismissMs` and own the setTimeout that
 *      clears the success banner — moves dismissal policy out of each
 *      consumer's useEffect.
 *   3. Optional `formatRowCount: (count: number) => string` slot for
 *      consumers that want "N slots exported" vs "N rows exported".
 *   4. Optionally render via a portal into a shared toast region so
 *      every export across the app uses the same screen real estate.
 *
 * Keeping it as a simple presentational component for now — the consumer
 * still owns startExport, resetExport, and the timing of dismissal —
 * since the existing call sites already have those wired and the goal
 * here is to standardize the visual layer first.
 */
interface ServerExportStatusBannerProps {
  status: ServerExportStatus;
  /**
   * Called when the user dismisses the banner. Wire to `resetExport` from
   * `useServerExport`. Dismissing while processing abandons the export, so a
   * lost completion event can't leave it stuck. When omitted, the banner is
   * not dismissible (close button hidden).
   */
  onDismiss?: () => void;
  /** Override copy for the processing state. */
  processingTitle?: string;
  /** Override copy for the processing state. */
  processingSubtitle?: string;
  /** Override copy for the success state. Subtitle is row-count derived. */
  successTitle?: string;
  /** Override copy for the failure state. Subtitle is the server error. */
  failureTitle?: string;
}

const DEFAULT_PROCESSING_TITLE = 'Exporting…';
const DEFAULT_PROCESSING_SUBTITLE =
  'Your file will download automatically when ready.';
const DEFAULT_SUCCESS_TITLE = 'Export ready';
const DEFAULT_FAILURE_TITLE = "Couldn't export.";

const ServerExportStatusBanner = ({
  status,
  onDismiss,
  processingTitle = DEFAULT_PROCESSING_TITLE,
  processingSubtitle = DEFAULT_PROCESSING_SUBTITLE,
  successTitle = DEFAULT_SUCCESS_TITLE,
  failureTitle = DEFAULT_FAILURE_TITLE,
}: ServerExportStatusBannerProps) => {
  if (status.status === 'idle') return null;

  if (status.status === 'processing') {
    return (
      <InlineNotification
        kind="info"
        role="status"
        lowContrast
        hideCloseButton={!onDismiss}
        onCloseButtonClick={onDismiss}
        title={processingTitle}
        subtitle={processingSubtitle}
      />
    );
  }

  if (status.status === 'complete') {
    const subtitle =
      status.rowCount !== null
        ? `${format0(status.rowCount)} ${
            status.rowCount === 1 ? 'row' : 'rows'
          } exported.`
        : 'Your file is downloading.';
    return (
      <InlineNotification
        kind="success"
        role="status"
        lowContrast
        hideCloseButton={!onDismiss}
        onCloseButtonClick={onDismiss}
        title={successTitle}
        subtitle={subtitle}
      />
    );
  }

  return (
    <InlineNotification
      kind="error"
      role="alert"
      lowContrast
      hideCloseButton={!onDismiss}
      onCloseButtonClick={onDismiss}
      title={failureTitle}
      subtitle={status.error ?? 'An unknown error occurred.'}
    />
  );
};

export default ServerExportStatusBanner;
